import type {
  Helper, HelperCounts, HelperDetail, HelperList, HelperRun, HelperState, HelperStep, SessionStatus,
} from "./types";

/**
 * Pure presentation logic for the Helpers watcher (topbar chip, the note under
 * the latest reply, the Helpers sheet and one helper's detail). Everything the
 * server sends is re-validated here, so a malformed or older payload degrades
 * to "fewer rows", never to a claim that is not true.
 */

export type HelperTone = "needs" | "failed" | "working" | "done";

const STATES: readonly HelperState[] = ["needs_you", "failed", "running", "stopped", "done"];
export const STATE_WORD: Record<HelperState, string> = {
  running: "Working", needs_you: "Needs you", failed: "Failed", stopped: "Stopped", done: "Done",
};

export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

function face(value: unknown): number | undefined {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 && value < HELPER_FACES ? value : undefined;
}

function count(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

/**
 * Helper counts for a chat from its /all-status entry. The helper watcher's
 * `helpers` counts are the truth; an older backend only has the sub-agent
 * heuristic, which is used for running/failed and nothing else.
 */
export function helperCountsFromStatus(status: SessionStatus | null | undefined): HelperCounts | null {
  if (!status || status.state !== "running") return null;
  const h = status.helpers;
  if (h && typeof h === "object") {
    const c: HelperCounts = {
      running: count(h.running), needsYou: count(h.needsYou), failed: count(h.failed),
      done: count(h.done), stopped: count(h.stopped), total: 0,
    };
    c.total = Math.max(count(h.total), c.running + c.needsYou + c.failed + c.done + c.stopped);
    const faces = Array.isArray(h.faces) ? h.faces.map(face).filter((n): n is number => n !== undefined).slice(0, 3) : [];
    if (faces.length) c.faces = faces;
    return c.total ? c : null;
  }
  const running = count(status.subagents?.running), failed = count(status.subagents?.failed);
  if (!running && !failed) return null;
  return { running, needsYou: 0, failed, done: 0, stopped: 0, total: running + failed };
}

export interface HelpersChipView {
  tone: HelperTone;
  /** Full words for the topbar. */
  text: string;
  /** Short words for a phone topbar. */
  short: string;
  /** Screen-reader label, including what tapping does. */
  label: string;
}

/**
 * Topbar chip. Highest need wins the colour (needs you › failed › working ›
 * done), but a chip never hides helpers that are still working: those stay in
 * the words, so a red or amber chip cannot read as "everything finished".
 */
export function helpersChip(c: HelperCounts | null | undefined): HelpersChipView | null {
  if (!c || !c.total) return null;
  const working = c.running;
  const alsoWorking = working ? ` · ${working} working` : "";
  const alsoLabel = working ? `, ${plural(working, "helper")} still working` : "";
  if (c.needsYou) {
    return {
      tone: "needs", text: `Needs you${alsoWorking}`, short: "Needs you",
      label: `${plural(c.needsYou, "helper")} need${c.needsYou === 1 ? "s" : ""} you${alsoLabel}. Show helpers`,
    };
  }
  if (c.failed) {
    const text = working ? `${c.failed} failed${alsoWorking}` : `${plural(c.failed, "helper")} failed`;
    return {
      tone: "failed", text, short: working ? `${c.failed} failed${alsoWorking}` : `${c.failed} failed`,
      label: `${plural(c.failed, "helper")} failed${alsoLabel}. Show helpers`,
    };
  }
  if (working) {
    return {
      tone: "working", text: `${plural(working, "helper")} working`, short: `${working} working`,
      label: `${plural(working, "helper")} working. Show helpers`,
    };
  }
  const both = c.done && c.stopped;
  const text = both ? `${c.done} done · ${c.stopped} stopped`
    : c.done ? `${plural(c.done, "helper")} done` : `${plural(c.stopped, "helper")} stopped`;
  const short = both ? text : c.done ? `${c.done} done` : `${c.stopped} stopped`;
  return { tone: "done", text, short, label: `${text}. Show helpers` };
}

/**
 * The line under the latest reply. Says only what is true right now: nothing
 * running and nothing failed means no line at all.
 */
export function helpersReplyNote(c: HelperCounts | null | undefined, readingResults = false): { tone: HelperTone; text: string; short: string; working: number; faces: number[] } | null {
  // Helpers finished and the agent is working on their results in a turn of its own:
  // say so, so a finished helper list is never mistaken for "no answer coming".
  if (readingResults && !(c && (c.running || c.needsYou)))
    return { tone: "working", text: "A helper finished — the agent is reading its results. The answer is on the way.",
      short: "Reading results · reply coming", working: 0, faces: [] };
  if (!c) return null;
  const parts: string[] = [];
  if (c.needsYou) parts.push(`${plural(c.needsYou, "helper")} need${c.needsYou === 1 ? "s" : ""} you.`);
  if (c.running) parts.push(`${plural(c.running, "helper")} still working — more replies may follow.`);
  if (c.failed) parts.push(`${plural(c.failed, "helper")} failed.`);
  if (!parts.length) return null;
  const tone: HelperTone = c.needsYou ? "needs" : c.failed ? "failed" : "working";
  // The pill's words: the most urgent fact first, working helpers after it.
  const lead = c.needsYou ? `${plural(c.needsYou, "helper")} need${c.needsYou === 1 ? "s" : ""} you`
    : c.failed ? `${plural(c.failed, "helper")} failed` : "";
  const working = c.running ? (lead ? `${c.running} working` : `${plural(c.running, "helper")} working`) : "";
  const short = [lead, working].filter(Boolean).join(" · ");
  return { tone, text: parts.join(" "), short, working: c.running, faces: c.faces ?? [] };
}

/* ------------------------------------------------------------------ faces */

/**
 * Helpers wear libraries.dev bot avatars, like profile pictures. Faces 0-7 are the eight
 * shapes in their own colours; 8-15 the same shapes in a second colour. The server hands
 * the faces out (distinct inside a group); an older server sends none, so the id's hash
 * picks one the same way.
 */
export const HELPER_BOT_SHAPES = ["clover", "flower", "star", "ghost", "mech", "circle", "hexagon", "square"] as const;
export type HelperBotShape = typeof HELPER_BOT_SHAPES[number];
const HELPER_FACES = HELPER_BOT_SHAPES.length * 2;
const SECOND_COLOUR: Record<HelperBotShape, string> = {
  clover: "#FFD32B", flower: "#35B8FF", star: "#FF8C42", ghost: "#1ED3C6",
  mech: "#FF8C42", circle: "#1ED3C6", hexagon: "#FFD32B", square: "#2FCB7A",
};

function hashId(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return h >>> 0;
}

export function helperFace(bot: number | undefined, id = ""): { type: HelperBotShape; color?: string } {
  const n = face(bot) ?? hashId(id) % HELPER_FACES;
  const type = HELPER_BOT_SHAPES[n % HELPER_BOT_SHAPES.length];
  return n < HELPER_BOT_SHAPES.length ? { type } : { type, color: SECOND_COLOUR[type] };
}

/**
 * The job most helpers of a group share (eight designers all "Build ONE finished static
 * ad…"): said once under the group's heading, so each row only carries a job of its own.
 * Only when more than half the group (and at least two) share it.
 */
export function groupTask(helpers: Helper[]): string | undefined {
  const seen = new Map<string, number>();
  for (const h of helpers) if (h.task) seen.set(h.task, (seen.get(h.task) ?? 0) + 1);
  let best: string | undefined, most = 1;
  for (const [task, n] of seen) if (n > most) { best = task; most = n; }
  return most * 2 > helpers.length ? best : undefined;
}

export function stateTone(state: HelperState): HelperTone {
  return state === "needs_you" ? "needs" : state === "failed" ? "failed" : state === "running" ? "working" : "done";
}

export function isActive(state: HelperState): boolean {
  return state === "running" || state === "needs_you";
}

/* ------------------------------------------------------------------ time */

/** "12s", "4m", "3h", "2d". Negative or unknown reads as "0s". */
export function formatAge(ms: number): string {
  const s = Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 1000) : 0;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

/** "45s", "4m 12s", "1h 5m". */
export function formatDuration(ms: number): string {
  const s = Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 1000) : 0;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

export function timeOf(iso: string | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

/** "Tab 10 · Uploading a file · 12s ago" (falls back to the last write). */
export function actionLine(h: Helper, now: number): string {
  const { text, age } = actionParts(h, now);
  return text && age ? `${text} · ${age}` : text || age;
}

/** The row's action line in two parts: the words (they swap when the helper moves on) and the ticking age. */
export function actionParts(h: Helper, now: number): { text: string; age: string } {
  const at = timeOf(h.lastAction?.at) ?? timeOf(h.lastWriteAt);
  const age = at === null ? "" : `${formatAge(now - at)} ago`;
  const text = h.lastAction?.text?.trim();
  if (text) return { text, age };
  if (!age) return { text: "", age: "" };
  return h.state === "running" ? { text: "Started", age } : { text: "", age: `Last update ${age}` };
}

/** Elapsed while working; how long it took once finished. */
export function helperTime(h: Helper, now: number): string {
  const start = timeOf(h.startedAt);
  if (isActive(h.state)) return start === null ? "" : formatDuration(now - start);
  if (typeof h.durationMs === "number" && Number.isFinite(h.durationMs) && h.durationMs >= 0) return formatDuration(h.durationMs);
  const end = timeOf(h.lastWriteAt);
  return start !== null && end !== null && end >= start ? formatDuration(end - start) : "";
}

/** "quiet 4m" for a working helper that has been silent a while. */
export function quietHint(h: Helper, sinceFetchMs = 0): string {
  if (h.state !== "running" || typeof h.quietMs !== "number" || !Number.isFinite(h.quietMs) || h.quietMs <= 0) return "";
  return `quiet ${formatAge(h.quietMs + Math.max(0, sinceFetchMs))}`;
}

/** "Started 12m ago · 4m 12s" for the detail header. */
export function startedLine(h: Helper, now: number): string {
  const start = timeOf(h.startedAt);
  const time = helperTime(h, now);
  const parts = [start === null ? "" : `Started ${formatAge(now - start)} ago`,
    time ? (isActive(h.state) ? `working ${time}` : `took ${time}`) : ""].filter(Boolean);
  return parts.join(" · ");
}

/* ------------------------------------------------------------------ groups */

export interface HelperGroup {
  key: string;
  kind: "needs" | "failed" | "run" | "plain";
  title: string;
  /** "5 of 8 done" for run/plain groups; the count for pinned groups. */
  summary: string;
  helpers: Helper[];
  /** Something in this group is still working or needs you. */
  active: boolean;
  /** Pinned groups (Needs you, Failed) are never collapsed. */
  collapsible: boolean;
}

/**
 * Needs you and Failed are pinned first (from any run), then one group per
 * workflow run + phase ("Design · 5 of 8 done"), plus "Helpers" for plain
 * agent helpers. Groups with work still going come first, then the most recent.
 * Helper order inside a group is the server's (needs you, failed, running,
 * stopped, done).
 */
export function groupHelpers(helpers: Helper[], runs: HelperRun[] = []): HelperGroup[] {
  const groups: HelperGroup[] = [];
  const needs = helpers.filter(h => h.state === "needs_you");
  const failed = helpers.filter(h => h.state === "failed");
  if (needs.length) groups.push({ key: "needs", kind: "needs", title: "Needs you", summary: String(needs.length), helpers: needs, active: true, collapsible: false });
  if (failed.length) groups.push({ key: "failed", kind: "failed", title: "Failed", summary: String(failed.length), helpers: failed, active: false, collapsible: false });

  const runName = new Map(runs.filter(r => r.name).map(r => [r.run, r.name as string]));
  const runIds = new Set(helpers.filter(h => h.kind === "workflow").map(h => h.run ?? ""));
  const buckets = new Map<string, { kind: "run" | "plain"; title: string; all: Helper[] }>();
  for (const h of helpers) {
    const workflow = h.kind === "workflow";
    const key = workflow ? `run:${h.run ?? ""}:${h.phase ?? ""}` : "plain";
    let bucket = buckets.get(key);
    if (!bucket) {
      const name = runName.get(h.run ?? "") || "Workflow";
      const title = !workflow ? "Helpers"
        : [runIds.size > 1 || !h.phase ? name : "", h.phase ?? ""].filter(Boolean).join(" · ");
      bucket = { kind: workflow ? "run" : "plain", title, all: [] };
      buckets.set(key, bucket);
    }
    bucket.all.push(h);
  }
  const rest: HelperGroup[] = [];
  const latest = new Map<string, number>();
  for (const [key, b] of buckets) {
    const listed = b.all.filter(h => h.state !== "needs_you" && h.state !== "failed");
    if (!listed.length) continue;
    const done = b.all.filter(h => h.state === "done").length;
    rest.push({
      key, kind: b.kind, title: b.title, summary: `${done} of ${b.all.length} done`, helpers: listed,
      active: b.all.some(h => isActive(h.state)), collapsible: true,
    });
    latest.set(key, Math.max(...b.all.map(h => timeOf(h.lastWriteAt) ?? 0)));
  }
  rest.sort((a, b) => Number(b.active) - Number(a.active) || (latest.get(b.key) ?? 0) - (latest.get(a.key) ?? 0));
  return [...groups, ...rest];
}

/* ------------------------------------------------------------------ steps */

/**
 * Where a poll re-reads from: a little of what is already loaded, so running steps
 * can settle. `since` (that step's id) is what the server pages by; `after` is the
 * same place by position, for an older server without step ids.
 */
export function pollAnchor(loaded: HelperStep[]): { after: number; since?: string } {
  const after = Math.max(0, loaded.length - 20);
  return { after, since: loaded[after]?.id };
}

/**
 * Put a fetched page into the loaded list. By step id: once a helper's transcript
 * passes the server's read window its positions shift with every write, so a step
 * already loaded is updated where it is, never dropped, and new ones are appended.
 * By position (fetched at `offset`) only when a step has no id (an older server).
 */
export function mergeSteps(old: HelperStep[], offset: number, page: HelperStep[]): HelperStep[] {
  if (!page.every(s => s.id) || !old.every(s => s.id)) {
    const at = Math.max(0, Math.min(offset, old.length));
    return [...old.slice(0, at), ...page, ...old.slice(at + page.length)];
  }
  const merged = [...old];
  const index = new Map(old.map((s, i) => [s.id, i]));
  for (const step of page) {
    const at = index.get(step.id);
    if (at === undefined) { index.set(step.id, merged.length); merged.push(step); } else merged[at] = step;
  }
  return merged;
}

/** The first `lines` lines of the brief (and at most ~80 characters a line). */
export function briefPreview(brief: string, lines = 6): { text: string; clipped: boolean } {
  const all = brief.split("\n");
  let text = all.slice(0, lines).join("\n").trimEnd();
  const maxChars = lines * 80;
  // An ellipsis only where a sentence is cut; whole lines hidden are shown by "Show all".
  const midLine = text.length > maxChars;
  if (midLine) text = text.slice(0, maxChars).replace(/\s+\S*$/, "") + "…";
  return { text, clipped: midLine || all.length > lines && brief.trimEnd() !== text };
}

/* ------------------------------------------------------------------ parsing */

function str(value: unknown, max = 2000): string | undefined {
  return typeof value === "string" ? value.slice(0, max) : undefined;
}
function num(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

export function parseHelper(value: unknown): Helper | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  const id = str(v.id, 128);
  const state = STATES.find(s => s === v.state);
  if (!id || !state) return null;
  const lastAction = v.lastAction && typeof v.lastAction === "object"
    ? { text: str((v.lastAction as Record<string, unknown>).text, 300) ?? "", at: str((v.lastAction as Record<string, unknown>).at, 64) }
    : undefined;
  return {
    id, state, kind: v.kind === "workflow" ? "workflow" : "agent",
    run: str(v.run, 128), name: str(v.name, 200)?.trim() || "Helper", phase: str(v.phase, 120)?.trim() || undefined,
    task: str(v.task, 200)?.trim() || undefined, bot: face(v.bot), type: str(v.type, 120), startedAt: str(v.startedAt, 64), lastWriteAt: str(v.lastWriteAt, 64) ?? "",
    quietMs: num(v.quietMs), waitingOnToolSince: str(v.waitingOnToolSince, 64),
    lastAction: lastAction?.text ? lastAction : undefined,
    durationMs: num(v.durationMs), toolUses: num(v.toolUses),
  };
}

export function parseHelperList(body: unknown): HelperList {
  const b = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;
  const helpers = Array.isArray(b.helpers) ? b.helpers.map(parseHelper).filter((h): h is Helper => !!h) : [];
  const runs = Array.isArray(b.runs) ? b.runs.flatMap((r): HelperRun[] => {
    if (!r || typeof r !== "object") return [];
    const v = r as Record<string, unknown>;
    const run = str(v.run, 128);
    return run ? [{ run, name: str(v.name, 200), status: str(v.status, 40) ?? "", agents: num(v.agents) ?? 0, lastWriteAt: str(v.lastWriteAt, 64) ?? "" }] : [];
  }) : [];
  return { alive: b.alive === true, helpers, runs };
}

function parseStep(value: unknown): HelperStep | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  if (v.kind !== "text" && v.kind !== "tool") return null;
  const status = v.status === "running" || v.status === "ok" || v.status === "error" ? v.status : undefined;
  const img = v.image && typeof v.image === "object" ? v.image as Record<string, unknown> : null;
  const image = img && typeof img.url === "string" && img.url.length <= 600 ? { url: img.url, alt: str(img.alt, 200) || "Image" } : null;
  const id = str(v.id, 120);
  return {
    ...(id ? { id } : {}),
    kind: v.kind, ts: str(v.ts, 64), name: str(v.name, 120), words: str(v.words, 400), label: str(v.label, 1200),
    ...(status ? { status } : {}), ...(v.needsYou === true ? { needsYou: true } : {}),
    ...(image ? { image } : {}),
  };
}

/**
 * The only image URL a helper step may load: this chat's, this helper's image route,
 * with exactly the server's own parameters. Anything else renders nothing.
 */
export function safeHelperImage(url: string | undefined, companyId: string, target: { id: string; run?: string }): string | null {
  if (!url || url.includes("#")) return null;
  const prefix = `/api/sessions/${encodeURIComponent(companyId)}/helpers/${encodeURIComponent(target.id)}/image?`;
  if (!url.startsWith(prefix)) return null;
  const params = new URLSearchParams(url.slice(prefix.length));
  const keys = [...params.keys()].sort().join(",");
  if (keys !== (target.run ? "block,part,run,uuid" : "block,part,uuid")) return null;
  if (target.run && params.get("run") !== target.run) return null;
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(params.get("uuid") ?? "")) return null;
  return /^\d{1,2}$/.test(params.get("block") ?? "") && /^\d{1,2}$/.test(params.get("part") ?? "") ? url : null;
}

export function parseHelperDetail(body: unknown): HelperDetail | null {
  if (!body || typeof body !== "object") return null;
  const b = body as Record<string, unknown>;
  const helper = parseHelper(b.helper);
  if (!helper) return null;
  const steps = Array.isArray(b.steps) ? b.steps.map(parseStep).filter((s): s is HelperStep => !!s) : [];
  const nextAfter = num(b.nextAfter);
  return {
    helper, brief: str(b.brief, 20000) ?? "", steps, total: num(b.total) ?? steps.length,
    truncated: b.truncated === true, result: str(b.result, 20000) || undefined,
    nextAfter: nextAfter === undefined ? null : nextAfter,
  };
}
